
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, LocalizedLink } from '../primitives';
import { ArrowRight } from '../layout/buttons/ArrowRight';
import SiteLocaleSelectionPopupMenu from '../siteLocale/SiteLocaleSelectionPopupMenu';

type SidenavMenuProps = {
    productOpen: boolean;
    partOpen: boolean;
    supportOpen: boolean;
    setProductOpen: (isProductOpen: boolean) => void;
    setPartOpen: (isPartOpen: boolean) => void;
    setSupportOpen: (isSupportOpen: boolean) => void;
    onSetSidebarOpen: (isSidebarOpen: boolean) => void;
};

export default function SidenavMenu(props: SidenavMenuProps) {
    const {
        productOpen,
        partOpen,
        supportOpen,
        setProductOpen,
        setPartOpen,
        setSupportOpen,
        onSetSidebarOpen,
    } = props;
    const { t } = useTranslation('mainLayout');

    return (
        <div>
            <Button
                className='sidenavClose'
                label='X'
                onClick={() => onSetSidebarOpen(false)}
            />

            <LocalizedLink to='/'>
                <h3 className='sidenavTitle'>{t('centuryheating')}</h3>
            </LocalizedLink>

            <div className='contactGroup'>
                <a href='tel:1-877-356-6663'>
                    <Icon
                        d='M.068,4.425A2.921,2.921,0,0,0,.362,5.7,25.873,25.873,0,0,0,5.33,12.608a25.871,25.871,0,0,0,6.905,4.969A2.948,2.948,0,0,0,15.605,17l2.328-2.344L13.148,9.9l-1.908,1.884A13.5,13.5,0,0,1,8.423,9.6,13.293,13.293,0,0,1,6.254,6.8L8.139,4.854,3.277,0,.936,2.333A2.958,2.958,0,0,0,.068,4.425ZM1.921,3.32,3.276,1.97l2.9,2.9-1.6,1.657.189.429A13.264,13.264,0,0,0,7.436,10.59a13.5,13.5,0,0,0,3.647,2.687l.435.194,1.626-1.6,2.817,2.8-1.344,1.353a1.552,1.552,0,0,1-1.772.3,24.46,24.46,0,0,1-6.528-4.7,24.461,24.461,0,0,1-4.7-6.528A1.553,1.553,0,0,1,1.921,3.32Z'
                        size='18'
                        viewBox='0 0 17.865 17.87'
                        transform='translate(-0.068)'
                    />
                    1-877-356-6663
                </a>
            </div>

            <div className='contactGroup'>
                <LocalizedLink to='/contact-us/'>
                    <>
                        <Icon
                            d='M0,88v13.74H18.562V88Zm16.705.977L9.281,95.645,1.857,88.977Zm-15.618.691,5.793,5.2-5.793,5.2Zm.769,11.1,5.793-5.2,1.631,1.465,1.631-1.465,5.793,5.2Zm15.618-.691-5.793-5.2,5.793-5.2Z'
                            size='18'
                            viewBox='0 0 18.562 13.74'
                            transform='translate(0 -88)'
                        />
                        {t('contact-us')}
                    </>
                </LocalizedLink>
            </div>

            <div className='sidenavMenu'>
                <div>
                    <p
                        className='pointer'
                        style={
                            productOpen
                                ? { marginBottom: '1rem' }
                                : { marginBottom: 0 }
                        }
                        onClick={() => setProductOpen(!productOpen)}
                    >
                        {t('products')}
                        {!productOpen && (
                            <span>
                                <ArrowRight />
                            </span>
                        )}
                    </p>
                    {productOpen && (
                        <div className='productsMenu'>
                            <LocalizedLink to='/wood-stoves/'>
                                <ArrowRight intlKey='stoves' />
                            </LocalizedLink>
                            <LocalizedLink to='/wood-inserts/'>
                                <ArrowRight intlKey='inserts' />
                            </LocalizedLink>
                        </div>
                    )}
                </div>

                <div>
                    <p
                        className='pointer'
                        style={
                            partOpen
                                ? { marginBottom: '1rem' }
                                : { marginBottom: 0 }
                        }
                        onClick={() => setPartOpen(!partOpen)}
                    >
                        {t('parts')}
                        {!partOpen && (
                            <span>
                                <ArrowRight />
                            </span>
                        )}
                    </p>
                    {partOpen && (
                        <div className='productsMenu'>
                            <LocalizedLink to='/archives/'>
                                <ArrowRight intlKey='parts-only' />
                            </LocalizedLink>
                            <LocalizedLink to='/accessories/'>
                                <ArrowRight intlKey='accessories' />
                            </LocalizedLink>
                            <LocalizedLink to='/venting/'>
                                <ArrowRight intlKey='venting' />
                            </LocalizedLink>
                        </div>
                    )}
                </div>

                <div>
                    <p
                        className='pointer'
                        style={
                            supportOpen
                                ? { marginBottom: '1rem' }
                                : { marginBottom: 0 }
                        }
                        onClick={() => setSupportOpen(!supportOpen)}
                    >
                        {t('support')}
                        {!supportOpen && (
                            <span>
                                <ArrowRight />
                            </span>
                        )}
                    </p>
                    {supportOpen && (
                        <div className='productsMenu'>
                            <LocalizedLink to='/articles/'>
                                <ArrowRight intlKey='articles' />
                            </LocalizedLink>
                            <LocalizedLink to='/videos/'>
                                <ArrowRight intlKey='videos' />
                            </LocalizedLink>
                            <LocalizedLink to='/warranty/'>
                                <ArrowRight intlKey='warranty' />
                            </LocalizedLink>
                            <LocalizedLink to='/archives/'>
                                <ArrowRight intlKey='manuals-only' />
                            </LocalizedLink>
                            <LocalizedLink to='/installer/'>
                                <ArrowRight intlKey='findinstaller' />
                            </LocalizedLink>
                            <LocalizedLink to='/about/'>
                                <ArrowRight intlKey='about' />
                            </LocalizedLink>
                            <LocalizedLink to='/government-incentives/'>
                                <ArrowRight intlKey='gov-incentives' />
                            </LocalizedLink>
                            <LocalizedLink to='/contact-us/'>
                                <ArrowRight intlKey='contact' />
                            </LocalizedLink>
                            <LocalizedLink to='/order-status/'>
                                <ArrowRight intlKey='order-status-title' />
                            </LocalizedLink>
                        </div>
                    )}
                </div>

                <LocalizedLink to='/promotions/'>
                    <div>
                        {t('promotions')}
                        <span>
                            <ArrowRight />
                        </span>
                    </div>
                </LocalizedLink>

                <div className='languageSelector'>
                    <SiteLocaleSelectionPopupMenu />
                </div>
            </div>
        </div>
    );
}
